import Button from 'components/ui/Button/Button';
import FloatingLabelInput from 'components/ui/Inputs/FloatingLabelInput';
import { usePageContext } from 'context/hooks/usePageContext';
import { useTransactionContext } from 'context/hooks/useTransactionContext';
import { useCardContext } from 'context/hooks/useCardContext';
import { PAGE_SUB } from 'context/pageContext';
import useGetCardPaymentStatus from 'hooks/useGetCardPaymentStatus';
import React, { useState } from 'react';
import { checkResponse, debounce, getResponseMessage } from 'utils';
import axiosInstance from 'utils/axiosInstance';
import { validateAddress } from 'utils/validation';
import { getApiResponseMessage } from 'services/paymentHandlers/apiError';

const AddressAuth = ({
  setUrl,
}: {
  setUrl: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const { changeSub, changePage } = usePageContext();
  const { ceviantTransactionId } = useTransactionContext();
  const { changeCardDetails } = useCardContext();
  const [addressForm, setAddressForm] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [addressErrors, setAddressErrors] = useState<Record<string, string>>(
    {},
  );
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isAddressSubmitting, setIsAddressSubmitting] = useState(false);
  const { triggerFetch, error } = useGetCardPaymentStatus(
    ceviantTransactionId,
    { shouldFetch },
    `/payments/status`,
  );

  const validateAddressFormDebounced = debounce((form: typeof addressForm) => {
    const { errors } = validateAddress(form);
    setAddressErrors(errors);
  }, 500);

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAddressForm((prevForm) => {
      const updatedForm = { ...prevForm, [name]: value };
      validateAddressFormDebounced(updatedForm);
      return updatedForm;
    });
  };

  const handleAddressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateAddress(addressForm);
    if (validation.isValid) {
      try {
        setIsAddressSubmitting(true);

        const payload = {
          address: addressForm.address,
          city: addressForm.city,
          state: addressForm.state,
          zipCode: addressForm.zipCode,
          reference: ceviantTransactionId,
        };

        const response = await axiosInstance.post(
          '/payments/addressAuths',
          payload,
        );

        if (response.data) {
          if (response.data.url) {
            setUrl(response.data.url);
          }
          changeCardDetails({
            otpPrompt: response.data.metadata?.displayText ?? '',
          });
          checkResponse(
            response.data.response,
            changeSub,
            changePage,
            getResponseMessage(response.data),
          );
        }
      } catch (error) {
        // The API rejected the request and said why (e.g. "Invalid OTP"),
        // so show that rather than polling a status that hides it.
        const apiMessage = getApiResponseMessage(error);
        if (apiMessage) {
          changeSub(PAGE_SUB.ERROR, apiMessage);
          return;
        }

        setShouldFetch(true);
        triggerFetch();
      } finally {
        setIsAddressSubmitting(false);
      }
    } else {
      setAddressErrors(validation.errors);
    }
  };

  if (error) {
    changeSub(PAGE_SUB.ERROR);
  }

  return (
    <div className="space-y-6 text-center py-14">
      <img
        src="/icons/authenticate.svg"
        alt=""
        className="m-auto w-11"
      />
      <h3 className="text-[#535353] text-lg font-semibold">
        Please input your address to continue your payment
      </h3>
      <form
        className="flex flex-col gap-5 px-4"
        onSubmit={handleAddressSubmit}
      >
        <FloatingLabelInput
          label="Address"
          type="text"
          value={addressForm.address}
          onChange={handleAddressChange}
          name="address"
        />
        {addressErrors.address && (
          <p className="text-red-500 text-sm mt-1">{addressErrors.address}</p>
        )}
        <FloatingLabelInput
          label="City"
          type="text"
          value={addressForm.city}
          onChange={handleAddressChange}
          name="city"
        />
        {addressErrors.city && (
          <p className="text-red-500 text-sm mt-1">{addressErrors.city}</p>
        )}
        <FloatingLabelInput
          label="State"
          type="text"
          value={addressForm.state}
          onChange={handleAddressChange}
          name="state"
        />
        {addressErrors.state && (
          <p className="text-red-500 text-sm mt-1">{addressErrors.state}</p>
        )}
        <FloatingLabelInput
          label="Zip Code"
          type="number"
          value={addressForm.zipCode}
          onChange={handleAddressChange}
          name="zipCode"
        />
        {addressErrors.zipCode && (
          <p className="text-red-500 text-sm mt-1">{addressErrors.zipCode}</p>
        )}
        <div className="flex gap-3 w-full m-auto">
          <Button
            type="submit"
            variant="primary"
            disabled={isAddressSubmitting}
          >
            Authenticate
          </Button>
          <Button
            variant="outline"
            type="button"
            onClick={() =>
              window.parent.postMessage({ message: 'closeCeviantSDK' }, '*')
            }
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddressAuth;
